import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';
import { API_ADMIN_MEDICATIONS } from 'services/dataService/resources/medications';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';

import type { IAdminMedicationDetails } from 'infrastructure/interfaces';

interface IUpdateMedicationPayload {
  otc?: boolean | null;
  manufacturer?: string | null;
  couponDescription?: string | null;
}

const updateMedicationFn = async (
  medicationGuid: string,
  payload: IUpdateMedicationPayload,
) => {
  const response = await dataService.patchOne<IAdminMedicationDetails>(
    API_ADMIN_MEDICATIONS,
    medicationGuid,
    payload,
  );

  if (response.error) {
    throw new Error(response.error);
  }

  return response.data;
};

export const useUpdateMedicationDetail = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      medicationGuid,
      payload,
    }: {
      medicationGuid: string;
      payload: IUpdateMedicationPayload;
    }) => updateMedicationFn(medicationGuid, payload),
  });

  const updateMedication = (
    variables: { medicationGuid: string; payload: IUpdateMedicationPayload },
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    },
  ) => {
    mutation.mutate(variables, {
      onSuccess: (_, vars) => {
        notificationController.success({
          message: 'Medication updated successfully',
        });
        queryClient.invalidateQueries([
          'adminMedicationDetails',
          vars.medicationGuid,
        ]);
        options?.onSuccess?.();
      },

      onError: (error: unknown) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to update medication';
        showResult(errorMessage);
        options?.onError?.(error as Error);
      },
    });
  };

  return {
    updateMedication,
    isLoading: mutation.isLoading,
  };
};
